import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  Tabs,
  TabsProps,
  Select,
  Row,
  Col,
} from "antd";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/images/logo.svg";
import authService from "../../services/auth-service.ts";
import userService from "../../services/user-service.ts";
import Constant from "../../global/Constant.ts";
import { useDispatch } from "react-redux";
import { setUserData, setUserLoggedIn } from "../../redux/authSlice.ts";
import { setUserDetails } from "../../redux/userSlice.ts";
import { setLoading, addNotification } from "../../redux/globalSlice.ts";
import "react-phone-input-2/lib/style.css";
import {
  signIn,
  confirmSignIn,
  confirmSignUp,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
  resendSignUpCode,
} from "aws-amplify/auth";
import { Amplify } from "aws-amplify";
import awscognitpool from "../../../src/global/awscognitpool.ts";
import countries from "world-countries";
import { decryptData } from "../../utils/decryptData.ts"; 
import { encryptData } from "../../utils/encryptData.ts";


Amplify.configure(awscognitpool);
const { Option } = Select;
const Authentication = ({ onClosewithoutNav, onClose }) => {
  const [activeTab, setActiveTab] = React.useState("1");
  const [form] = Form.useForm();
  const [loginform] = Form.useForm();
  const [resetPasswordForm] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userSession, setUserSession] = useState(null);
  const [additionalFieldLabel, setAdditionalFieldLabel] = useState(null);
  const [loading, setBtnLoading] = useState(false);
  const [error, setError] = useState("");
  const [userType, setUserType] = useState(null);
  const [resetPasswordStep, setResetPasswordStep] = useState("REQUEST"); // REQUEST or CONFIRM
  const [userEmail, setUserEmail] = useState("");
  const [showForgotPasswordTab, setShowForgotPasswordTab] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleSignIn = async () => {
    try {
      setBtnLoading(true);
      const values = await loginform.validateFields();

      if (!values.newpassword) {
        await handleRegularSignIn(values);
      } else {
        await handleChallengeResponse(values.newpassword);
      }
    } catch (err) {
      setError(err.message.replace("username", "email"));
    } finally {
      setBtnLoading(false);
    }
  };

  const handleRegularSignIn = async (values) => {
    const normalizedEmail = values.email.toLowerCase();
    const user = await signIn({
      username: normalizedEmail,
      password: values.password,
    });

    if (user?.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
      setAdditionalFieldLabel("Confirmation Code");
      setUserSession({ ...user, username: values.email });
      return;
    }

    if (
      user?.nextStep?.signInStep ===
      "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
    ) {
      setAdditionalFieldLabel("New Password");
      setUserSession(user);
      return;
    }

    await handleSuccessfulLogin(values.email);
  };

  const handleChallengeResponse = async (newpassword) => {
    try {
      setBtnLoading(true);
      if (userSession?.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
        await confirmSignUp({
          username: userSession.username,
          confirmationCode: newpassword,
        });
        // After successful confirmation, automatically log the user in
        await handleRegularSignIn({
          email: userSession.username,
          password: loginform.getFieldValue("password"),
        });
      } else {
        await confirmSignIn({
          user: userSession,
          challengeResponse: newpassword,
        });
        await handleSuccessfulLogin(userSession.username);
      }
      setUserSession(null);
    } catch (err) {
      setError(err.message.replace("username", "email"));
      throw err;
    } finally {
      setBtnLoading(false);
    }
  };

  const handleResendConfirmationCode = async (email) => {
    try {
      dispatch(setLoading(true));
      setResendDisabled(true);
      setCountdown(60);
      await resendSignUpCode({ username: email });

      dispatch(
        addNotification({
          title: "Success",
          description: "New confirmation code sent to your email.",
          type: "success",
        })
      );
    } catch (err) {
      setError(err.message);
      setResendDisabled(false);
      dispatch(
        addNotification({
          title: "Error",
          description: "Failed to resend confirmation code. Please try again.",
          type: "error",
        })
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSuccessfulLogin = async (email) => {
    const loggedUser = await getCurrentUser();
    const session = await fetchAuthSession();

    const userAttributes = extractUserAttributes(loggedUser, session);

    if (userAttributes.groups?.includes("admin")) {
      setError("Access Denied: Admins must log in through the admin portal.");
      await signOut();
      return;
    }

    const userDetailsEncrypted = await userService.getUserDetailsByEmail(
      Constant.userdetails
    );
    const userDetails = await decryptData(
      userDetailsEncrypted?.data,
      process.env.REACT_APP_ENCRYPTION_KEY!
    );

    if (userDetails.error || userDetails.Status === 0) {
      setError(
        "Your login attempt has been blocked. Please contact support for assistance."
      );
      await signOut();
      return;
    }

    dispatch(setUserDetails(userDetails));
    dispatch(
      addNotification({
        title: "Success",
        description: "Logged in successfully.",
        type: "success",
      })
    );
    dispatch(setUserLoggedIn());
    dispatch(setUserData(userAttributes));

    // Store session start time
    localStorage.setItem("sessionStartTime", new Date().getTime().toString());

    onClosewithoutNav();
  };

  const extractUserAttributes = (loggedUser, session) => {
    if (!session || !session.tokens) return loggedUser;

    const idToken = session.tokens.idToken;
    const payload = idToken.payload;

    return {
      ...loggedUser,
      groups: payload["cognito:groups"] || [],
    };
  };

  const handleAuthUser = async () => {
    try {
      setBtnLoading(true);
      const values = await form.validateFields();
      const normalizedEmail = values.email.toLowerCase();

      if (activeTab === "2") {
        const payload = {
          firstName: values.firstname,
          lastName: values.lastname,
          email: normalizedEmail,
          password: values.password,
          phoneNumber: values.phone,
          userType: parseInt(userType),
          clinicName: values.clinicName,
          expertiseLevel: values.expertise,
          country: values.country,
        };
  
        const encryptedPayload = await encryptData(payload, process.env.REACT_APP_ENCRYPTION_KEY!);
  
        await authService.signup(Constant.signUp, {
          data: encryptedPayload,
        });

        dispatch(
          setUserData({
            ...values,
            userType: parseInt(userType),
            email: normalizedEmail,
          })
        );
        navigate("/otp");

        dispatch(
          addNotification({
            title: "Success",
            description: "OTP successfully sent to email",
            type: "success",
          })
        );

        onClosewithoutNav();
      }
    } catch (err) {
      if (err?.response?.data?.message) {
        dispatch(
          addNotification({
            title: "Failed",
            description: err?.response?.data?.message,
            type: "error",
          })
        );
      } else {
        dispatch(
          addNotification({
            title: "Failed",
            description: "An unexpected error occurred. Please try again.",
            type: "error",
          })
        );
      }
      console.error("Error:", err);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    try {
      setBtnLoading(true);
      const values = await resetPasswordForm.validateFields();
      let { email } = values;
      email = email.toLowerCase();
      setUserEmail(email);

      const isUserExistsResponse = await userService.isUserExists(
        Constant.userlist,
        email
      );

      if (!isUserExistsResponse.isExist) {
        dispatch(
          addNotification({
            title: "Error",
            description: "No account found with this email address.",
            type: "error",
          })
        );
        return;
      }

      await resetPassword({ username: email });
      setResetPasswordStep("CONFIRM");
      setError("");
      dispatch(
        addNotification({
          title: "Success",
          description: "Reset password OTP sent to your email.",
          type: "success",
        })
      );
    } catch (err) {
      if (err.errorFields && err.errorFields.length > 0) {
        return;
      }

      dispatch(
        addNotification({
          title: err?.response?.data?.message ? "Info" : "Error",
          description:
            err?.response?.data?.message || "Failed to initiate password reset",
          type: err?.response?.data?.message ? "info" : "error",
        })
      );
    } finally {
      setBtnLoading(false);
    }
  };

  const handleConfirmResetPassword = async () => {
    try {
      setBtnLoading(true);
      const values = await resetPasswordForm.validateFields();
      const { code, newPassword } = values;

      await confirmResetPassword({
        username: userEmail,
        confirmationCode: code,
        newPassword,
      });

      setResetPasswordStep("REQUEST");
      setError("");
      dispatch(
        addNotification({
          title: "Success",
          description: "Password reset successfully. Please log in.",
          type: "success",
        })
      );
      setActiveTab("1");
      setShowForgotPasswordTab(false);
      loginform.resetFields();
      resetPasswordForm.resetFields();
    } catch (err) {
      if (!err.errorFields || err.errorFields.length === 0) {
        setError(err.message);
        resetPasswordForm.resetFields();
      }
    } finally {
      setBtnLoading(false);
    }
  };

  // Preferred countries list
  const preferredCountries = ["DE", "GB", "US"];

  // Sort countries to show preferred ones first
  const sortedCountries = [
    ...countries.filter((c) => preferredCountries.includes(c.cca2)),
    ...countries.filter((c) => !preferredCountries.includes(c.cca2)),
  ];

  const getCommonRules = (fieldName) => [{ whitespace: true, message: `` }];

  const login = (
    <Form
      layout="vertical"
      form={loginform}
      onFinish={handleSignIn}
      autoComplete="off"
    >
      <Form.Item
        label="Email"
        name="email"
        rules={[
          { required: true, message: "" },
          {
            type: "email",
            message: "",
          },
          ...getCommonRules("Email"),
        ]}
        className="vm__mb-2"
      >
        <Input
          placeholder="Enter your email"
          autoComplete="off"
          disabled={!!userSession}
        />
      </Form.Item>

      <Form.Item
        label="Password"
        name="password"
        rules={[{ required: true, message: "" }]}
        className="vm__mb-2"
      >
        <Input.Password
          placeholder="Enter your password"
          autoComplete="new-password"
          disabled={!!userSession}
        />
      </Form.Item>

      {userSession && (
        <>
          <Form.Item
            label={additionalFieldLabel || "Set New Password"}
            name="newpassword"
            rules={[
              {
                required: true,
                message: "",
              },
            ]}
            className="vm__mb-2"
          >
            <Input.Password
              placeholder={"Enter your " + additionalFieldLabel}
            />
          </Form.Item>

          {userSession?.nextStep?.signInStep === "CONFIRM_SIGN_UP" && (
            <div style={{ textAlign: "right", marginBottom: "16px" }}>
              <Button
                type="link"
                onClick={() =>
                  handleResendConfirmationCode(userSession.username)
                }
                disabled={resendDisabled || countdown > 0}
              >
                {countdown > 0
                  ? `Resend available in ${countdown}s`
                  : "Resend Confirmation Code"}
              </Button>
            </div>
          )}
        </>
      )}

      <Form.Item className="mb-sm vm__text-end vm__mb-2">
        <Button
          type="link"
          onClick={() => {
            setActiveTab("3");
            setShowForgotPasswordTab(true);
          }}
        >
          Forgot Password?
        </Button>
      </Form.Item>

      <Form.Item className="mb-sm vm__mb-0">
        <Button type="primary" htmlType="submit" block loading={loading}>
          Login
        </Button>
      </Form.Item>

      {error && (
        <Form.Item className="mb-sm vm__mb-0">
          <div style={{ color: "red" }}>
            {error || "Something went wrong. Please try again."}
          </div>
        </Form.Item>
      )}
    </Form>
  );

  const registration = (
    <Form
      layout="vertical"
      form={form}
      onFinish={handleAuthUser}
      autoComplete="off"
    >
      <div className="vm__d-flex vm__h-100 vm__flex-column">
        <div className="vm__overflow-auto vm__scroll-container">
          <Row gutter={16}>
            <Col xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                label="First Name"
                name="firstname"
                rules={[
                  { required: true, message: "" },
                  ...getCommonRules("First Name"),
                ]}
                className="vm__mb-2"
              >
                <Input placeholder="Enter your first name" autoComplete="off" />
              </Form.Item>
            </Col>
            <Col xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                label="Last Name"
                name="lastname"
                rules={[
                  { required: true, message: "" },
                  ...getCommonRules("Last Name"),
                ]}
                className="vm__mb-2"
              >
                <Input placeholder="Enter your last name" autoComplete="off" />
              </Form.Item>
            </Col>
            <Col xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "" },
                  {
                    type: "email",
                    message: "",
                  },
                ]}
                className="vm__mb-2"
              >
                <Input placeholder="Enter your email" autoComplete="off" />
              </Form.Item>
            </Col>

            <Col xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                name="country"
                label="Country"
                rules={[{ required: true, message: "" }]}
                className="vm__mb-2"
              >
                <Select
                  placeholder="Select a country"
                  showSearch
                  optionFilterProp="children"
                >
                  {sortedCountries.map((country) => (
                    <Option key={country.cca2} value={country.name.common}>
                      <img
                        src={`https://flagcdn.com/w40/${country.cca2.toLowerCase()}.png`} // Fetch flag using country code
                        alt={country.name.common}
                        style={{ width: 20, marginRight: 8 }}
                      />
                      {country.name.common}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                label="Phone"
                name="phone"
                rules={[
                  {
                    pattern: /^(\+?\d{7,16})$/,
                    message: "Enter a valid phone number",
                  },
                ]}
                className="vm__mb-2"
              >
                <Input
                  placeholder="Enter your phone number"
                  autoComplete="off"
                  maxLength={16} // Allows + and up to 15 digits
                  onKeyPress={(e) => {
                    if (!/[\d+]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const pastedData = e.clipboardData.getData("text");
                    if (!/^\+?\d+$/.test(pastedData)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>

            <Col xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                label="Password"
                name="password"
                className="vm__mb-2"
                rules={[
                  { required: true, message: "" },
                  {
                    min: 8,
                    message: "Password must be at least 8 characters long",
                  },
                  { max: 32, message: "Password cannot exceed 32 characters" },
                ]}
              >
                <Input.Password
                  placeholder="Enter your password"
                  autoComplete="new-password"
                />
              </Form.Item>
            </Col>
            <Col xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                label="Confirm Password"
                name="confirmPassword"
                dependencies={["password"]}
                className="vm__mb-2"
                rules={[
                  { required: true, message: "" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Passwords do not match")
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                />
              </Form.Item>
            </Col>
            <Col xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                label="Expertise"
                name="expertise"
                rules={[{ required: true, message: "" }]}
                className="vm__mb-2"
              >
                <Select
                  showSearch
                  placeholder="Select Expertise"
                  options={[
                    { value: "1", label: "Embryologist" },
                    { value: "2", label: "Clinician" },
                    { value: "3", label: "Distributor" },
                    { value: "4", label: "Professional" },
                    { value: "5", label: "VITROMED Employee" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                label="User Type"
                name="userType"
                rules={[{ required: true, message: "" }]}
                className="vm__mb-2"
              >
                <Select
                  showSearch
                  placeholder="Select User Type"
                  onChange={(value) => setUserType(value)}
                  options={[
                    { value: "2", label: "Group" },
                    { value: "0", label: "Individual" },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                label="Organization Name"
                name="clinicName"
                rules={[
                  { required: true, message: "" },
                  ...getCommonRules("Clinic Name"),
                ]}
              >
                <Input
                  placeholder="Enter your organization name"
                  autoComplete="off"
                />
              </Form.Item>
            </Col>
          </Row>
        </div>
        <div className="vm__flex-auto vm__pt-2">
          <Button type="primary" htmlType="submit" block loading={loading}>
            Register
          </Button>
        </div>
      </div>
    </Form>
  );

  const forgotPassword = (
    <Form
      layout="vertical"
      form={resetPasswordForm}
      onFinish={
        resetPasswordStep === "REQUEST"
          ? handleForgotPassword
          : handleConfirmResetPassword
      }
      autoComplete="off"
      className="vm__if-login"
    >
      {resetPasswordStep === "REQUEST" ? (
        <>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "" },
              {
                type: "email",
                message: "",
              },
            ]}
            className="vm__mb-2"
          >
            <Input placeholder="Enter your email" autoComplete="off" />
          </Form.Item>

          <Form.Item className="mb-sm">
            <Button
              type="primary"
              onClick={handleForgotPassword}
              block
              loading={loading}
            >
              Send Reset OTP
            </Button>
          </Form.Item>
        </>
      ) : (
        <>
      <Form.Item
        label="Reset OTP"
        name="code"
        rules={[
          { required: true, message: "OTP is required" },
          {
            validator: (_, value) => {
              if (!value) {
                return Promise.resolve(); 
              }
              if (/^\d{6}$/.test(value)) {
                return Promise.resolve();
              }
              return Promise.reject("OTP must be a 6-digit number");
            },
          },
        ]}
        className="vm__mb-2"
      >
      <Input
        placeholder="Enter the 6 digit OTP"
        autoComplete="off"
        maxLength={6}
        onKeyPress={(event) => {
          if (!/\d/.test(event.key)) {
            event.preventDefault();
          }
        }}
      />
      </Form.Item>

          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              { required: true, message: "" },
              {
                min: 8,
                message: "Password must be at least 8 characters long!",
              },
              { max: 32, message: "Password cannot exceed 32 characters!" },
            ]}
            className="vm__mb-2"
          >
            <Input.Password
              placeholder="Enter your new password"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item className="mb-sm">
            <Button
              type="primary"
              onClick={handleConfirmResetPassword}
              block
              loading={loading}
            >
              Reset Password
            </Button>
          </Form.Item>
        </>
      )}

      {error && (
        <Form.Item className="mb-sm">
          <div style={{ color: "red", marginBottom: "10px" }}>
            {error || "Something went wrong. Please try again."}
          </div>
        </Form.Item>
      )}
    </Form>
  );

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "Login",
      children: login,
      className: "vm__if-login",
    },
    {
      key: "2",
      label: "Registration",
      children: registration,
    },
    ...(showForgotPasswordTab
      ? [
          {
            key: "3",
            label: "Forgot Password",
            children: forgotPassword,
          },
        ]
      : []),
  ];

  const onChange = (key: string) => {
    setError("");
    setUserSession(null);
    loginform.resetFields();
    form.resetFields();
    resetPasswordForm.resetFields();
    setActiveTab(key);
    setResetPasswordStep("REQUEST");
    if (key === "1" || key === "2") {
      setShowForgotPasswordTab(false);
    }
  };

  return (
    <Modal
      visible={true}
      onCancel={() => {
        onClose();
        setUserSession(null);
        setShowForgotPasswordTab(false);
      }}
      onOk={handleAuthUser}
      width={676}
      footer={null}
      maskClosable={false}
    >
      <div className="vm__logo-container">
        <img src={logo} alt="Logo" />
      </div>

      <Tabs
        activeKey={activeTab}
        defaultActiveKey="1"
        items={items}
        onChange={onChange}
      />
    </Modal>
  );
};

export default Authentication;
